import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import React, { useMemo } from 'react';
import type { ParcelDetail, NearestFeature, NearbyPipeline } from '../../lib/api';

interface SchematicPin {
  key: string;
  label: string;
  distance_miles: number | null;
  lng: number;
  lat: number;
  mark: string;       // single letter shown in the legend & pin
  accent?: boolean;   // hero pin (burnt sienna)
  ok?: boolean;       // moss green
  shape?: 'circle' | 'square';
}

interface Props {
  parcel: ParcelDetail;
  pipelines: NearbyPipeline[];
  nearestFeatures?: {
    gasPlant: NearestFeature | null;
    railroad: NearestFeature | null;
    substation: NearestFeature | null;
    secondSubstation: NearestFeature | null;
    airport: NearestFeature | null;
  };
}

// ---------- projection helpers ----------------------------------------------

function bboxOf(coords: number[][]): { minLon: number; maxLon: number; minLat: number; maxLat: number } {
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLon, maxLon, minLat, maxLat };
}

// Flatten any Polygon / MultiPolygon ring into [lon,lat] coord arrays
function flattenPolygon(geom: GeoJSON.Geometry | undefined): number[][][] {
  if (!geom) return [];
  if (geom.type === 'Polygon') return geom.coordinates as number[][][];
  if (geom.type === 'MultiPolygon') {
    const rings: number[][][] = [];
    for (const poly of geom.coordinates as number[][][][]) {
      for (const ring of poly) rings.push(ring);
    }
    return rings;
  }
  return [];
}

// Title-case airport names and append FAA code
function humanizeAirport(name: string | undefined, faaId?: string): string {
  if (!name) return 'Airport';
  const titled = name.split(/\s+/).map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ').replace(/\bWv\b/gi, 'WV').replace(/\bRgnl\b/gi, 'Rgnl');
  return faaId ? `${titled} (${faaId})` : titled;
}

// Humanize railroad abbreviations
function humanizeRail(abbr: string | undefined): string {
  const map: Record<string, string> = { CSXT: 'CSX Transportation', BNSF: 'BNSF Railway', UP: 'Union Pacific', NS: 'Norfolk Southern', CN: 'Canadian National', CP: 'Canadian Pacific' };
  if (!abbr) return 'Rail';
  return map[abbr.toUpperCase()] || abbr;
}

// ---------- component --------------------------------------------------------

export const SchematicSVG: React.FC<Props> = ({ parcel, pipelines, nearestFeatures }) => {
  const centroidLng = Number(parcel.lng);
  const centroidLat = Number(parcel.lat);

  const rings = useMemo(() => flattenPolygon(parcel.geojson), [parcel.geojson]);
  const primaryRing = rings[0] || [];

  // Real bbox — fall back to a small box centered on the centroid when the parcel
  // has no geometry (shouldn't happen in prod but keeps the component safe).
  const bbox = primaryRing.length
    ? bboxOf(primaryRing)
    : { minLon: centroidLng - 0.01, maxLon: centroidLng + 0.01, minLat: centroidLat - 0.01, maxLat: centroidLat + 0.01 };

  // Expand bbox to fit the further pins so everything on the canvas is visible.
  // We'll finalize it after we compute pins.

  // Canvas & projection setup
  const W = 600;
  const H = 440;

  // Collect candidate pins from real API data
  const pins: SchematicPin[] = [];

  // Hero pipeline (nearest with best capacity signal)
  const heroPipeline = pipelines && pipelines.length > 0 ? pipelines[0] : null;
  // Pipelines don't carry lat/lng in the briefing response — we approximate
  // the tap position by placing it at the parcel edge closest to the pipeline
  // distance. To do that we just use a fixed bearing offset from centroid.
  // This is intentionally schematic, not survey-grade.

  const substation = nearestFeatures?.substation;
  if (substation) {
    pins.push({
      key: 'substation',
      label: `${substation.max_voltage_kv ? `${substation.max_voltage_kv} kV` : ''} substation${substation.name && !/^UNKNOWN/i.test(substation.name) ? ` · ${substation.name}` : ''}`.trim(),
      distance_miles: substation.distance_miles,
      lng: substation.lng,
      lat: substation.lat,
      mark: 'B',
      shape: 'square',
    });
  }
  const secondSub = nearestFeatures?.secondSubstation;
  if (secondSub && secondSub.distance_miles !== substation?.distance_miles) {
    pins.push({
      key: 'second_sub',
      label: `Secondary substation${secondSub.name && !/^UNKNOWN/i.test(secondSub.name) ? ` · ${secondSub.name}` : ''}`,
      distance_miles: secondSub.distance_miles,
      lng: secondSub.lng,
      lat: secondSub.lat,
      mark: 'B′',
      shape: 'square',
    });
  }
  const rail = nearestFeatures?.railroad;
  if (rail) {
    pins.push({
      key: 'rail',
      label: `${humanizeRail(rail.operator)} rail`,
      distance_miles: rail.distance_miles,
      lng: rail.lng,
      lat: rail.lat,
      mark: 'D',
    });
  }
  const gasPlant = nearestFeatures?.gasPlant;
  if (gasPlant && gasPlant.distance_miles != null && Number(gasPlant.distance_miles) < 10) {
    pins.push({
      key: 'gas_plant',
      label: `${gasPlant.name || 'Gas plant'}`,
      distance_miles: gasPlant.distance_miles,
      lng: gasPlant.lng,
      lat: gasPlant.lat,
      mark: 'G',
    });
  }
  const airport = nearestFeatures?.airport;
  if (airport && airport.distance_miles != null && Number(airport.distance_miles) < 8) {
    pins.push({
      key: 'airport',
      label: humanizeAirport(airport.name, airport.faa_id),
      distance_miles: airport.distance_miles,
      lng: airport.lng,
      lat: airport.lat,
      mark: 'F',
    });
  }

  // Fiber on-site pin — placed at the centroid. When the FCC hex distance is
  // non-zero, "on-site" (let alone "enterprise") is self-contradicting — label
  // by what the data actually shows.
  const hasFiber = parcel.fiber_has_enterprise === true || (parcel.fiber_provider_count != null && parcel.fiber_provider_count > 0);
  if (hasFiber) {
    const fccPinMi = parcel.fiber_nearest_miles != null ? Number(parcel.fiber_nearest_miles) : null;
    pins.push({
      key: 'fiber',
      label: fccPinMi != null && fccPinMi > 0.05 ? 'FCC fiber coverage' : 'Enterprise fiber on-site',
      distance_miles: parcel.fiber_nearest_miles ?? 0,
      lng: centroidLng,
      lat: centroidLat,
      mark: 'C',
      ok: true,
    });
  }

  // Determine the final bbox that fits parcel, pins, AND pipeline tap points
  let { minLon, maxLon, minLat, maxLat } = bbox;
  for (const pin of pins) {
    if (pin.lng < minLon) minLon = pin.lng;
    if (pin.lng > maxLon) maxLon = pin.lng;
    if (pin.lat < minLat) minLat = pin.lat;
    if (pin.lat > maxLat) maxLat = pin.lat;
  }
  // Include pipeline tap coords in the bbox (only those within 8 mi to keep scale tight)
  const seenOps = new Set<string>();
  for (const pipe of pipelines) {
    const op = pipe.operator || pipe.name;
    if (seenOps.has(op)) continue;
    seenOps.add(op);
    if (seenOps.size > 3) break;
    if (pipe.tap_lng != null && pipe.tap_lat != null && Number(pipe.distance_miles) <= 8) {
      const tLng = Number(pipe.tap_lng);
      const tLat = Number(pipe.tap_lat);
      if (tLng < minLon) minLon = tLng;
      if (tLng > maxLon) maxLon = tLng;
      if (tLat < minLat) minLat = tLat;
      if (tLat > maxLat) maxLat = tLat;
    }
  }
  // 8% padding
  const lonPad = (maxLon - minLon) * 0.10 || 0.005;
  const latPad = (maxLat - minLat) * 0.10 || 0.005;
  minLon -= lonPad; maxLon += lonPad;
  minLat -= latPad; maxLat += latPad;

  const avgLatRad = (minLat + maxLat) / 2 * Math.PI / 180;
  const lonMiPerDeg = 69 * Math.cos(avgLatRad);
  const latMiPerDeg = 69;

  const wMi = (maxLon - minLon) * lonMiPerDeg;
  const hMi = (maxLat - minLat) * latMiPerDeg;

  const padding = 40; // inner canvas padding (labels need room)
  const usableW = W - padding * 2;
  const usableH = H - padding * 2;
  const scale = Math.min(usableW / wMi, usableH / hMi);

  // Actual drawn area in px
  const drawnW = wMi * scale;
  const drawnH = hMi * scale;
  const offsetX = (W - drawnW) / 2;
  const offsetY = (H - drawnH) / 2;

  const project = (lng: number, lat: number): [number, number] => {
    const x = offsetX + (lng - minLon) * lonMiPerDeg * scale;
    // y axis is flipped in SVG
    const y = offsetY + (maxLat - lat) * latMiPerDeg * scale;
    return [x, y];
  };

  const [cx, cy] = project(centroidLng, centroidLat);

  // Parcel polygon points string
  const polygonPoints = primaryRing
    .map(([lon, lat]) => {
      const [x, y] = project(lon, lat);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // Pipeline tap coordinates: use real closest-point from API when available.
  // When tap_lng/tap_lat are present we can draw the line through the actual tap position;
  // perpendicular to the vector from centroid → tap gives a plausible sweep direction.

  // Scale bar — show ~ 1 mi reference
  const oneMiPx = scale; // px per mile
  let scaleLen = 1; // mi
  if (oneMiPx * scaleLen < 40) scaleLen = 2;
  if (oneMiPx * scaleLen < 40) scaleLen = 5;

  // Compass — simple north arrow

  // Pin rendering
  const pinRadius = 9;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" aria-label="Site schematic" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <pattern id="sv2-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(23,20,14,0.06)" strokeWidth="0.5" />
        </pattern>
        <pattern id="sv2-gridmaj" width="200" height="200" patternUnits="userSpaceOnUse">
          <path d="M 200 0 L 0 0 0 200" fill="none" stroke="rgba(23,20,14,0.12)" strokeWidth="0.5" />
        </pattern>
        <pattern id="sv2-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="5" stroke="rgba(181,71,43,0.22)" strokeWidth="0.7" />
        </pattern>
        <marker id="sv2-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#17140E" />
        </marker>
      </defs>

      <rect width={W} height={H} fill="url(#sv2-grid)" />
      <rect width={W} height={H} fill="url(#sv2-gridmaj)" />

      {/* North arrow */}
      <g transform={`translate(${W - 44}, 28)`} fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="10" fill="#17140E">
        <line x1={0} y1={26} x2={0} y2={2} stroke="#17140E" strokeWidth="1" markerEnd="url(#sv2-arrow)" />
        <text x={-4} y={42} letterSpacing="1">N</text>
      </g>

      {/* Scale bar */}
      <g transform={`translate(34, ${H - 28})`} fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="9" fill="#17140E">
        <line x1={0} y1={0} x2={oneMiPx * scaleLen} y2={0} stroke="#17140E" strokeWidth="1.2" />
        <line x1={0} y1={-4} x2={0} y2={4} stroke="#17140E" strokeWidth="1.2" />
        <line x1={oneMiPx * scaleLen} y1={-4} x2={oneMiPx * scaleLen} y2={4} stroke="#17140E" strokeWidth="1.2" />
        <text x={0} y={18} letterSpacing="0.5">0</text>
        <text x={oneMiPx * scaleLen - 12} y={18} letterSpacing="0.5">{scaleLen} MI</text>
      </g>

      {/* Lat tick labels — top + bottom */}
      <g fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="8" fill="rgba(23,20,14,0.45)" letterSpacing="1">
        <text x={W / 2} y={14} textAnchor="middle">{maxLat.toFixed(3)}°N</text>
        <text x={W / 2} y={H - 4} textAnchor="middle">{minLat.toFixed(3)}°N</text>
      </g>

      {/* Pipelines — angled lines through real tap coordinates */}
      {(() => {
        const seen = new Set<string>();
        const allPipes = pipelines.filter((p) => {
          const op = p.operator || p.name;
          if (seen.has(op)) return false;
          seen.add(op);
          return true;
        }).slice(0, 3);

        // Stagger angles so parallel pipelines don't overlap
        const baseAngles = [15, -20, 35];

        return allPipes.map((pipe, i) => {
          const isHero = i === 0;
          const tapLng = pipe.tap_lng != null ? Number(pipe.tap_lng) : null;
          const tapLat = pipe.tap_lat != null ? Number(pipe.tap_lat) : null;

          if (tapLng == null || tapLat == null) return null;

          const [tx, ty] = project(tapLng, tapLat);
          // Draw a line through the tap point at a gentle angle (pipeline corridor direction)
          const angle = (baseAngles[i] || 0) * Math.PI / 180;
          const sweep = Math.max(W, H) * 0.8;
          const dx = Math.cos(angle) * sweep;
          const dy = Math.sin(angle) * sweep;

          const distLabel = Number(pipe.distance_miles) <= 0.05 ? 'ON SITE' : `${Number(pipe.distance_miles).toFixed(2)} MI`;
          const capacity = pipelineCapacityDisplay(pipe);
          const pipeCap = capacity.capacityMmcf;
          const label = `${(pipe.operator || pipe.name || '').toUpperCase()} · ${distLabel}${pipe.diameter_inches ? ` · ${pipe.diameter_inches}″` : ''}${pipeCap ? ` · ${Math.round(pipeCap).toLocaleString()} MMcf/d${capacity.basis === 'diameter_model' ? ' screen' : ''}` : ''}`;

          return (
            <g key={`pipe-${i}`} opacity={isHero ? 1 : 0.5}>
              <title>{`${capacity.label}: ${capacity.explanation}`}</title>
              <line
                x1={tx - dx} y1={ty - dy}
                x2={tx + dx} y2={ty + dy}
                stroke={isHero ? '#B5472B' : '#17140E'}
                strokeWidth={isHero ? 2.4 : 1.4}
                strokeDasharray={isHero ? undefined : '7 3'}
              />
              <text
                x={Math.max(10, tx - dx * 0.35)}
                y={Math.max(18, ty - dy * 0.35 - 6)}
                fontFamily="ui-monospace, SF Mono, Menlo, monospace"
                fontSize={isHero ? 9 : 8}
                fill={isHero ? '#B5472B' : '#17140E'}
                fontWeight={isHero ? 600 : 400}
                letterSpacing="0.4"
              >
                {label}
              </text>
            </g>
          );
        });
      })()}

      {/* Parcel polygon */}
      {polygonPoints && (
        <polygon
          fill="url(#sv2-hatch)"
          stroke="#B5472B"
          strokeWidth="1.8"
          strokeLinejoin="round"
          points={polygonPoints}
        />
      )}

      {/* Parcel centroid + label */}
      <text x={cx + 14} y={cy - 6} fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="9" fill="#17140E" letterSpacing="0.4" fontWeight={600}>
        {(parcel.parcel_id_local || 'PARCEL').toUpperCase().slice(0, 30)} · {parcel.acreage ? `${parcel.acreage.toLocaleString()} AC` : ''}
      </text>
      <text x={cx + 14} y={cy + 6} fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="8" fill="rgba(23,20,14,0.55)" letterSpacing="0.4">
        {centroidLat.toFixed(4)}°N · {Math.abs(centroidLng).toFixed(4)}°W
      </text>

      {/* Pipeline tap pin — A (at real closest-point on hero pipeline) */}
      {heroPipeline && heroPipeline.tap_lng != null && heroPipeline.tap_lat != null && (() => {
        const [tapX, tapY] = project(Number(heroPipeline.tap_lng), Number(heroPipeline.tap_lat));
        const safeX = Math.max(16, Math.min(W - 16, tapX));
        const safeY = Math.max(16, Math.min(H - 16, tapY));
        return (
          <g transform={`translate(${safeX}, ${safeY})`}>
            <circle r={10} fill="#F2EDE1" stroke="#B5472B" strokeWidth={2} />
            <text y={4} textAnchor="middle" fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="10" fontWeight={700} fill="#B5472B">A</text>
          </g>
        );
      })()}

      {/* Other pins */}
      {pins.map((pin) => {
        const [px, py] = project(pin.lng, pin.lat);
        const color = pin.accent ? '#B5472B' : pin.ok ? '#506B2E' : '#17140E';
        const safeX = Math.max(16, Math.min(W - 16, px));
        const safeY = Math.max(16, Math.min(H - 16, py));
        if (pin.shape === 'square') {
          return (
            <g key={pin.key} transform={`translate(${safeX}, ${safeY})`}>
              <rect x={-9} y={-9} width={18} height={18} fill="#F2EDE1" stroke={color} strokeWidth={1.6} />
              <text y={3} textAnchor="middle" fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="9" fontWeight={700} fill={color}>{pin.mark}</text>
            </g>
          );
        }
        return (
          <g key={pin.key} transform={`translate(${safeX}, ${safeY})`}>
            <circle r={pinRadius} fill="#F2EDE1" stroke={color} strokeWidth={1.8} />
            <text y={3} textAnchor="middle" fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="9" fontWeight={700} fill={color}>{pin.mark}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Export a companion pin legend renderer so the content component can render
// the list next to the SVG using consistent marks + labels.
export function buildLegendEntries(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[],
  nearestFeatures?: {
    gasPlant: NearestFeature | null;
    railroad: NearestFeature | null;
    substation: NearestFeature | null;
    secondSubstation: NearestFeature | null;
    airport: NearestFeature | null;
  },
  compressor?: { name: string | null; operator: string | null; horsepower: number | null; num_units: number | null; distance_miles: number | string } | null,
  heroOnSite: boolean = false,
  // Crown Pine A1 only (caller passes it off payload.premises, which no other
  // briefing carries). The line CROSSES the proposed premises, so "<operator>
  // tap · 1.35 mi" asserted a tap that does not exist and contradicted the
  // crossing. Supplying this swaps row A for the crossing framing and carries
  // both distances — on-premises crossing and the analyzed-core offset.
  crossing?: { crossingMi: number } | null,
): Array<{ mark: string; label: string; dist: string; dist2?: string; color?: string }> {
  const out: Array<{ mark: string; label: string; dist: string; dist2?: string; color?: string }> = [];
  const hero = pipelines && pipelines.length > 0 ? pipelines[0] : null;
  if (hero && crossing) {
    out.push({
      mark: 'A',
      label: 'ET Texoma 30″ crossing · candidate tap area subject to ET approval',
      dist: `~${crossing.crossingMi.toFixed(2)} mi within proposed premises`,
      dist2: `${Number(hero.distance_miles).toFixed(2)} mi from analyzed core`,
      color: '#B5472B',
    });
  } else if (hero) {
    out.push({
      mark: 'A',
      // "(hero)" was internal jargon leaking into the client-facing legend.
      label: `${hero.operator || hero.name} tap`,
      dist: heroOnSite ? 'on site' : `${Number(hero.distance_miles).toFixed(2)} mi`,
      color: '#B5472B',
    });
  }
  // A′ / A″ — redundant-operator pipelines drawn on the schematic. Identity comes
  // from the legend (no text-on-line), matching the convention used by every other pin.
  // Gathering pipelines are skipped — they're local feeders, not BTM tap candidates,
  // and listing them as "redundancy" misrepresents what the IC reader is looking at.
  {
    const seen = new Set<string>([hero?.operator || hero?.name || '']);
    const extraMarks = ['A′', 'A″'];
    let idx = 0;
    for (const p of pipelines) {
      if (idx >= extraMarks.length) break;
      if ((p as any).is_gathering === true) continue;
      const key = p.operator || p.name || '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({
        mark: extraMarks[idx],
        label: p.operator || p.name || 'Pipeline',
        dist: `${Number(p.distance_miles).toFixed(2)} mi`,
      });
      idx++;
    }
  }
  const sub = nearestFeatures?.substation;
  if (sub) {
    const volts = sub.max_voltage_kv ? `${sub.max_voltage_kv} kV` : '';
    const sName = sub.name && !/^UNKNOWN/i.test(sub.name) ? sub.name : '';
    out.push({
      mark: 'B',
      label: [volts, 'substation', sName].filter(Boolean).join(' · '),
      dist: `${Number(sub.distance_miles).toFixed(2)} mi`,
    });
  }
  // Fiber — neutral "on site" label only. Enterprise vs residential grading is not
  // made at the memo level (FCC BDC has_enterprise column uses older brc logic and
  // can't be trusted for this claim). Carrier backbone row still surfaces independently.
  const hasFiber = parcel.fiber_provider_count != null && parcel.fiber_provider_count > 0;
  const carrierName = parcel.nearest_carrier_fiber_name || parcel.carrier_fiber_nearest?.[0]?.carrier || null;
  const carrierMi = parcel.nearest_carrier_fiber_miles ?? parcel.carrier_fiber_nearest?.[0]?.nearest_mi ?? null;
  if (hasFiber) {
    // "Fiber on site … 6.52 mi" is self-contradicting when the FCC hex distance is
    // non-zero; label by what the row actually is in that case.
    const fccMi = parcel.fiber_nearest_miles != null ? Number(parcel.fiber_nearest_miles) : null;
    out.push({
      mark: 'C',
      label: fccMi != null && fccMi > 0.05 ? 'FCC fiber coverage' : 'Fiber on site',
      dist: fccMi != null ? `${fccMi.toFixed(2)} mi` : 'on-site',
    });
    if (carrierName && carrierMi != null) {
      out.push({
        mark: 'C′',
        label: `${carrierName} carrier backbone`,
        dist: `${Number(carrierMi).toFixed(2)} mi`,
      });
    }
  } else if (carrierName && carrierMi != null) {
    out.push({
      mark: 'C',
      label: `${carrierName} fiber`,
      dist: `${Number(carrierMi).toFixed(2)} mi`,
    });
  }

  // D — railroad. Prefer parcel's precomputed value when the live KNN returns an
  // absurdly far result (usually means the railroads table has no coverage for
  // this state).
  const rail = nearestFeatures?.railroad;
  const parcelRailMi = parcel.nearest_railroad_miles != null ? Number(parcel.nearest_railroad_miles) : null;
  const railMi = rail?.distance_miles != null ? Number(rail.distance_miles) : null;
  if (parcelRailMi != null && (railMi == null || railMi > 50)) {
    out.push({
      mark: 'D',
      label: `${humanizeRail(parcel.nearest_railroad_owner || undefined)} rail`,
      dist: `${parcelRailMi.toFixed(2)} mi`,
    });
  } else if (rail && railMi != null && railMi < 50) {
    out.push({
      mark: 'D',
      label: `${humanizeRail(rail.operator)} rail`,
      dist: `${railMi.toFixed(2)} mi`,
    });
  }

  // E — nearest highway by name. Pulled from parcel.nearest_highway_name with
  // distance from parcel.nearest_highway_miles. 5 mi cap matches the schematic
  // pin gate so the legend never lists a road the schematic doesn't draw.
  if (parcel.nearest_highway_name && parcel.nearest_highway_miles != null && Number(parcel.nearest_highway_miles) < 5) {
    out.push({
      mark: 'E',
      label: parcel.nearest_highway_name,
      dist: `${Number(parcel.nearest_highway_miles).toFixed(2)} mi`,
    });
  }

  // F — compressor station (was E; reassigned when E became the highway slot).
  if (compressor && Number(compressor.distance_miles) <= 2) {
    const hp = compressor.horsepower ? `${Number(compressor.horsepower).toLocaleString()} HP` : '';
    const units = compressor.num_units ? `${compressor.num_units} units` : '';
    const detail = [hp, units].filter(Boolean).join(', ');
    out.push({
      mark: 'F',
      label: `${compressor.name || 'Compressor station'}${detail ? ` (${detail})` : ''}`,
      dist: `${Number(compressor.distance_miles).toFixed(2)} mi`,
    });
  }
  // (F airport row removed — pin no longer drawn on the schematic. Airport data
  // still lives in Sec. 06 Logistics block as text, where it actually informs the
  // operations narrative; pulling it out of the schematic legend keeps the map
  // focused on infrastructure that affects the BTM tap path.)

  const gp = nearestFeatures?.gasPlant;
  if (gp && gp.distance_miles != null && gp.distance_miles < 10) {
    out.push({
      mark: 'G',
      label: `Gas processing: ${gp.name || 'plant'}`,
      dist: `${Number(gp.distance_miles).toFixed(2)} mi`,
    });
  }
  return out;
}
